import { Component, HostListener, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { HttpService, MessageService } from 'src/app/core';
import { PathConfig } from 'src/app/core/config/path.config';
import { Utility } from 'src/app/core/utils/utility';
type Mode = 'light' | 'dark';
@Component({
  selector: 'app-pixel-editor',
  templateUrl: './pixel-editor.component.html',
  styleUrl: './pixel-editor.component.scss'
})
export class PixelEditorComponent {
  @Input() options: any = '';
  pixelsMap: any = { light: [], dark: [] };
  colorPallete = {
    primary: [
      '#ffffff', '#000000', '#d3d3d3', '#44546A', '#4472c4', '#ed7d31', '#a5a5a5',
      '#ffc000', '#5b9bd5', '#70ad47'
    ],
    variants: [
      ['#ecf0f1', '#d0d3d4', '#b3b6b7', '#909497', '#979a9a'],
      ['#5e5e5e', '#4b4b4b', '#383838', '#252525', '#121212'],
      ['#c0c0c0', '#a6a6a6', '#595959', '#404040', '#262626'],
      ['#AAB4C4', '#7A8CA3', '#44546A', '#2F3D4D', '#1B2733'],
      ['#d0e0f5', '#a0c0ea', '#709fdc', '#1c3c6c', '#102c4f'],
      ['#f7c59f', '#ee9a5c', '#d87d2c', '#b85f1d', '#8c4414'],
      ['#e0e0e0', '#b0b0b0', '#8e8e8e', '#6c6c6c', '#4a4a4a'],
      ['#fff0b3', '#ffe066', '#ffc533', '#e6a800', '#b38600'],
      ['#b7d4f0', '#94c1e4', '#6eaadc', '#1b6499', '#11477a'],
      ['#cbe6b4', '#a2d18a', '#7abf61', '#529d3b', '#3a7429']
    ]
  };
  selectedValues: any = {
    drawColor: '#70ad47',
    gridColor: '#D9D9D9',
    mode: 'light',
    gridSize: 20,
    isCleaning: false,
    draw: true,
  };

  gridModeColor: any = {
    light: '#D9D9D9',
    dark: '#212121'
  };

  previewImages: Record<Mode, string | null> = {
    light: null,
    dark: null
  };

  allModes: Mode[] = ['light', 'dark'];

  isIconCreated: any = {
    light: false,
    dark: false
  };

  @HostListener('window:mousedown') onMouseDown() { this.selectedValues.draw = true; };
  @HostListener('document:mouseup') onMouseUp() {
    if (this.selectedValues.draw) {
      this.selectedValues.draw = false;
      if (this.isIconCreated[this.selectedValues.mode]) {
        this.togglePreview();
      }
    }
  }

  @HostListener('document:dragstart')
  onDragStart() {
    this.selectedValues.draw = false;
  }

  get pixels(): string[] {
    return this.pixelsMap[this.selectedValues.mode];
  }

  set pixels(val: string[]) {
    this.pixelsMap[this.selectedValues.mode] = val;
  }

  constructor(
    private httpService: HttpService,
    private msgService: MessageService,
    private translateService: TranslateService,
  ) { }

  ngOnChanges() {
    this.populatePixels(); // initialize both modes
  }

  populatePixels() {
    this.selectedValues.draw = false;
    this.reset('component');
    if (this.options?.lightRawPixel) {
      this.pixelsMap.light = [...this.options.lightRawPixel];
      this.pixelsMap.dark = [...this.options.darkRawPixel];
      this.allModes.forEach(mode => this.togglePreview(mode));
      this.isIconCreated.light = true;
      this.isIconCreated.dark = true;
    } else {
      const defaultLight = Array(this.selectedValues.gridSize * this.selectedValues.gridSize).fill(this.gridModeColor.light);
      const defaultDark = Array(this.selectedValues.gridSize * this.selectedValues.gridSize).fill(this.gridModeColor.dark);
      this.pixelsMap.light = [...defaultLight];
      this.pixelsMap.dark = [...defaultDark];
    }
  }

  paintPixel(index: number) {
    this.isIconCreated[this.selectedValues.mode] = true;
    const paintColor = this.selectedValues.isCleaning ? this.selectedValues.gridColor : this.selectedValues.drawColor;
    this.pixels[index] = paintColor;
    this.pixels = [...this.pixels]; // trigger change detection
  }

  reset(from?: string) {
    const defaultColor = this.gridModeColor[this.selectedValues.mode];
    this.pixels = Array(this.selectedValues.gridSize * this.selectedValues.gridSize).fill(defaultColor);
    const mode: Mode = this.selectedValues.mode;
    this.previewImages[mode] = null;
    this.isIconCreated[mode] = false;
    if (from == 'component') {
      if (this.options?.lightRawPixel) {
        this.pixelsMap.light = [...this.options.lightRawPixel];
        this.pixelsMap.dark = [...this.options.darkRawPixel];
        this.allModes.forEach(mode => this.togglePreview(mode));
        this.isIconCreated.light = true;
        this.isIconCreated.dark = true;
      } else {
        this.allModes.forEach(mode => {
          this.previewImages[mode] = null;
          this.isIconCreated[mode] = null;
          this.pixelsMap[mode] = Array(this.selectedValues.gridSize * this.selectedValues.gridSize).fill(this.gridModeColor[mode]);
        });
      }
    }
  }

  toggleCleaning() {
    this.selectedValues.isCleaning = !this.selectedValues.isCleaning;
  }

  downloadImage() {
    const dataUrl = this.generateCanvasDataUrl(20);
    const link = document.createElement('a');
    link.download = `${this.selectedValues.mode}-pixel-art.png`;
    link.href = dataUrl;
    link.click();
  }

  generateCanvasDataUrl(scale: number, mode?: string): string {
    const currentMode = mode || this.selectedValues.mode;
    const pixels = this.pixelsMap[currentMode];
    const size = this.selectedValues.gridSize;

    const canvas = document.createElement('canvas');
    canvas.width = size * scale;
    canvas.height = size * scale;

    const ctx = canvas.getContext('2d')!;
    ctx.imageSmoothingEnabled = true;

    // Clip the drawing area to a circle
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = canvas.width / 2;

    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip(); // All drawing is now limited to the circle

    // Draw pixels
    pixels.forEach((color: string, i: number) => {
      const x = i % size;
      const y = Math.floor(i / size);
      ctx.fillStyle = color;
      ctx.fillRect(x * scale, y * scale, scale, scale);
    });

    return canvas.toDataURL();
  }


  togglePreview(modeParam?: Mode) {
    if (modeParam) {
      this.previewImages[modeParam] = this.generateCanvasDataUrl(10, modeParam);
    } else {
      const mode: Mode = this.selectedValues.mode;
      this.previewImages[mode] = this.generateCanvasDataUrl(10, mode);
    }
  }

  colorSelected(color: string) {
    this.selectedValues.drawColor = color;
  }

  modeSelected(mode: string) {
    this.selectedValues.mode = mode;
    this.selectedValues.gridColor = this.gridModeColor[mode];
  }

  async uploadGeneratedImage(iconChangeStatus?: { light: boolean; dark: boolean }) {
    return new Promise(async (resolve, reject) => {
      this.togglePreview();

      const modes = this.allModes.filter(mode =>
        !iconChangeStatus || iconChangeStatus[mode]
      );

      if (modes?.length === 0) {
        resolve({ message: 'No icons to upload' });
        return;
      }

      const uploadPromises = modes.map((mode) => {
        const dataUrl = this.previewImages[mode] || '';
        const blob = this.dataURLtoBlob(dataUrl);
        const file = new File([blob], `${mode}-pixel-art.png`, { type: 'image/png' });

        const formData = new FormData();
        formData.append('type', 'FileBuilder');
        formData.append('file', file, file.name);

        return this.httpService.upload(PathConfig.UPLOAD_MEDIA, formData).toPromise().then(response => ({
          mode,
          data: response,
        }));
      });

      try {
        const responses = await Promise.all(uploadPromises);

        const mappedResponses: Record<string, any> = {};
        responses.forEach(res => {
          this.msgService.showSuccess(res.data.message || `${this.translateService.instant(res.mode)} ${this.translateService.instant('icon uploaded successfully.')}`);
          mappedResponses[res.mode] = res.data;
        });

        resolve(mappedResponses);
      } catch (err: any) {
        const msgList = Utility.getErrorMessage(err.error || err);
        msgList.forEach((msg) => this.msgService.showError(msg));
        reject(0);
      }
    });
  }

  dataURLtoBlob(dataUrl: string): Blob {
    const arr = dataUrl.split(',');
    const mime = arr[0].match(/:(.*?);/)![1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new Blob([u8arr], { type: mime });
  }

  checkImageValidation() {
    let errMsg = '';
    if (!this.isIconCreated?.light && !this.isIconCreated?.dark) {
      errMsg = 'Please create light and dark icon.';
    } else if (!this.isIconCreated?.light) {
      errMsg = 'Please create light icon.';
    } else if (!this.isIconCreated?.dark) {
      errMsg = 'Please create dark icon.';
    }
    if (errMsg) {
      this.msgService.showError(errMsg);
      return false;
    } else {
      return true;
    }
  }

  getIconCreationStatus(): { light: boolean; dark: boolean } {
    const light = this.pixelsMap?.light?.some((pixel: any) => pixel !== this.gridModeColor.light) ?? false;
    const dark = this.pixelsMap?.dark?.some((pixel: any) => pixel !== this.gridModeColor.dark) ?? false;
    return { light, dark };
  }

  getIconChangeStatus(): { light: boolean; dark: boolean } {
    const light = this.pixelsMap?.light?.some((val: any, i: any) => val !== this.options?.lightRawPixel?.[i]) ?? false;
    const dark = this.pixelsMap?.dark?.some((val: any, i: any) => val !== this.options?.darkRawPixel?.[i]) ?? false;
    return { light, dark };
  }

  getPixelRawData() {
    return this.pixelsMap;
  }
}
